import React from 'react';
import { PiInfinityBold } from 'react-icons/pi';

const MISSING_ICON_SRC = `${process.env.PUBLIC_URL}/assets/missing_asset/missing_icon.png`;

const TONE_STYLES = {
    remake: {
        border: 'border-zinc-700',
        mutedBorder: 'border-[#33333a]',
        accentRail: 'bg-zinc-400/12',
        accentStripe: 'bg-zinc-300/70',
        badgeText: 'text-zinc-200',
        badgeBackground: 'bg-zinc-800/80',
        badgeBorder: 'border-zinc-700',
        badgeDot: 'bg-zinc-300',
        iconBackground: 'bg-zinc-800',
        itemBackground: 'bg-zinc-800',
        itemBackgroundSoft: 'bg-zinc-800',
        iconSurface: 'bg-zinc-950/90 border border-zinc-800',
        runeSurface: 'bg-zinc-950/90 border border-zinc-700',
        barTrack: 'bg-zinc-950/90 border-zinc-800',
        barFill: 'bg-zinc-700/80',
        accentText: 'text-zinc-300',
        panelSurface: 'bg-zinc-950/82',
        rowSurface: 'bg-zinc-900/55 border-zinc-800',
        focusPillSurface: 'bg-zinc-900/55 border-zinc-700',
        rowHighlightSurface: 'bg-zinc-950/94 border-zinc-700',
        previewHighlight: '!text-zinc-200',
    },
    victory: {
        border: 'border-cyan-700/40',
        mutedBorder: 'border-[#33333a]',
        accentRail: 'bg-cyan-400/14',
        accentStripe: 'bg-cyan-300/80',
        badgeText: 'text-cyan-200',
        badgeBackground: 'bg-cyan-900/20',
        badgeBorder: 'border-cyan-700/40',
        badgeDot: 'bg-cyan-300',
        iconBackground: 'bg-cyan-900',
        itemBackground: 'bg-zinc-900',
        itemBackgroundSoft: 'bg-zinc-800',
        iconSurface: 'bg-zinc-950/90 border border-cyan-900/25',
        runeSurface: 'bg-zinc-950/90 border border-cyan-800/35',
        barTrack: 'bg-zinc-950/90 border-cyan-900/25',
        barFill: 'bg-cyan-800/65',
        accentText: 'text-cyan-200',
        panelSurface: 'bg-cyan-950/15',
        rowSurface: 'bg-zinc-900/55 border-zinc-800',
        focusPillSurface: 'bg-zinc-900/55 border-cyan-800/55',
        rowHighlightSurface: 'bg-[#04161c] border-cyan-800/55',
        previewHighlight: '!text-cyan-300',
    },
    defeat: {
        border: 'border-rose-700/40',
        mutedBorder: 'border-[#33333a]',
        accentRail: 'bg-rose-400/14',
        accentStripe: 'bg-rose-300/80',
        badgeText: 'text-rose-200',
        badgeBackground: 'bg-rose-900/20',
        badgeBorder: 'border-rose-700/40',
        badgeDot: 'bg-rose-300',
        iconBackground: 'bg-rose-950',
        itemBackground: 'bg-zinc-900',
        itemBackgroundSoft: 'bg-zinc-800',
        iconSurface: 'bg-zinc-950/90 border border-rose-900/25',
        runeSurface: 'bg-zinc-950/90 border border-rose-800/35',
        barTrack: 'bg-zinc-950/90 border-rose-900/25',
        barFill: 'bg-rose-800/65',
        accentText: 'text-rose-200',
        panelSurface: 'bg-rose-950/15',
        rowSurface: 'bg-zinc-900/55 border-zinc-800',
        focusPillSurface: 'bg-zinc-900/55 border-rose-800/55',
        rowHighlightSurface: 'bg-[#1a0a0f] border-rose-800/55',
        previewHighlight: '!text-rose-300',
    },
};

function handleImageError(event) {
    if (event.currentTarget?.src !== MISSING_ICON_SRC) {
        event.currentTarget.src = MISSING_ICON_SRC;
    }
}

function isEmptyAsset(src) {
    return !src || src.includes('/0.png');
}

function formatDecimal(value, digits = 1) {
    const numericValue = Number(value || 0);
    const formatted = numericValue.toFixed(digits);
    return formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted;
}

export function getToneStyles(tone = 'victory') {
    return TONE_STYLES[tone] || TONE_STYLES.victory;
}

export function AssetImage({ alt, className = '', src, style, width, height }) {
    return (
        <img
            alt={alt}
            className={className}
            height={height}
            onError={handleImageError}
            src={src || MISSING_ICON_SRC}
            style={style}
            width={width}
        />
    );
}

export function SquareAsset({ alt, backgroundClassName = '', className = '', src, size = 22, tone = 'victory' }) {
    const toneStyles = getToneStyles(tone);
    const dimensions = { width: size, height: size, minWidth: size };
    const surfaceClassName = backgroundClassName || toneStyles.itemBackgroundSoft;

    if (isEmptyAsset(src)) {
        return (
            <div
                className={`rounded-md ${surfaceClassName} ${className}`}
                style={dimensions}
            />
        );
    }

    return (
        <AssetImage
            alt={alt}
            className={`rounded-md ${surfaceClassName} ${className}`}
            src={src}
            style={dimensions}
        />
    );
}

export function ChampionAvatar({ championIconSrc, championLevel, className = '', imageSize = 50, levelBadgeClassName = '', levelBadgeSize = 22, tone = 'victory' }) {
    const toneStyles = getToneStyles(tone);

    return (
        <div className={`relative shrink-0 overflow-visible ${className}`} style={{ width: imageSize, height: imageSize }}>
            <div className={`overflow-hidden rounded-full border-2 ${toneStyles.border}`} style={{ width: imageSize, height: imageSize }}>
                <AssetImage
                    alt="champion icon"
                    className="h-full w-full scale-[1.3] object-cover"
                    src={championIconSrc}
                />
            </div>
            <div
                className={`absolute -bottom-[1px] -right-[1px] flex items-center justify-center rounded-full font-semibold ${toneStyles.iconBackground} ${levelBadgeClassName}`}
                style={{ width: levelBadgeSize, height: levelBadgeSize, fontSize: Math.max(10, levelBadgeSize * 0.55) }}
            >
                <span className="mt-[1px]">{championLevel}</span>
            </div>
        </div>
    );
}

function renderFramedAsset({ alt, assetClassName = '', assetSize, src, surfaceClassName }) {
    const dimensions = { width: assetSize, height: assetSize, minWidth: assetSize };

    if (isEmptyAsset(src)) {
        return <div className={`overflow-hidden rounded-md ${surfaceClassName}`} style={dimensions} />;
    }

    return (
        <div className={`overflow-hidden rounded-md ${surfaceClassName}`} style={dimensions}>
            <AssetImage
                alt={alt}
                className={`h-full w-full ${assetClassName}`.trim()}
                src={src}
            />
        </div>
    );
}

export function SpellIcons({ className = '', size = 22, sources = [], tone = 'victory' }) {
    const toneStyles = getToneStyles(tone);
    const spellSurfaceClassName = toneStyles.runeSurface || toneStyles.iconSurface;

    return (
        <div className={`flex gap-0.5 ${className}`}>
            {sources.map((src, index) => (
                <React.Fragment key={`${src}-${index}`}>
                    {renderFramedAsset({
                    alt: "summoner spell icon",
                    assetClassName: 'object-cover',
                    assetSize: size,
                    src,
                    surfaceClassName: spellSurfaceClassName,
                })}
                </React.Fragment>
            ))}
        </div>
    );
}

export function RuneIcons({
    className = '',
    primaryClassName = '',
    primarySize = null,
    secondaryClassName = '',
    secondaryInsetClassName = '',
    secondarySize = null,
    size = 22,
    sources = {},
    tone = 'victory',
}) {
    const toneStyles = getToneStyles(tone);
    const resolvedPrimarySize = primarySize || size;
    const resolvedSecondarySize = secondarySize || size;
    const runeSurfaceClassName = toneStyles.runeSurface || toneStyles.iconSurface;

    return (
        <div className={`flex gap-0.5 ${className}`}>
            {renderFramedAsset({
                alt: 'primary rune icon',
                assetClassName: primaryClassName,
                assetSize: resolvedPrimarySize,
                src: sources.primary,
                surfaceClassName: runeSurfaceClassName,
            })}
            {renderFramedAsset({
                alt: 'secondary rune icon',
                assetClassName: `${secondaryInsetClassName} ${secondaryClassName}`.trim(),
                assetSize: resolvedSecondarySize,
                src: sources.secondary,
                surfaceClassName: runeSurfaceClassName,
            })}
        </div>
    );
}

export function AugmentGrid({ className = '', columns = 2, size = 22, sources = [], tone = 'victory' }) {
    const gridClass = columns === 3 ? 'grid-cols-3' : 'grid-cols-2';
    const toneStyles = getToneStyles(tone);

    return (
        <div className={`grid ${gridClass} gap-0.5 ${className}`}>
            {sources.map((src, index) => (
                <SquareAsset
                    alt="augment icon"
                    backgroundClassName={toneStyles.iconSurface}
                    key={`${src}-${index}`}
                    size={size}
                    src={src}
                    tone={tone}
                />
            ))}
        </div>
    );
}

export function SummaryItemGrid({ className = '', itemSources = [], roleBoundItemSource, size = 22, tone = 'victory', trinketSource }) {
    return (
        <div className={`flex gap-1 ${className}`}>
            {itemSources.slice(0, 6).map((src, index) => (
                <SquareAsset
                    alt="item icon"
                    key={`${src}-${index}`}
                    size={size}
                    src={src}
                    tone={tone}
                />
            ))}
            <SquareAsset alt="trinket icon" size={size} src={trinketSource} tone={tone} />
            <SquareAsset alt="role-bound item icon" size={size} src={roleBoundItemSource} tone={tone} />
        </div>
    );
}

export function ParticipantItemRow({ className = '', extraItemSource, includeExtraItem = true, itemSources = [], size = 20, tone = 'victory' }) {
    return (
        <div className={`flex gap-1 ${className}`}>
            {itemSources.map((src, index) => (
                <SquareAsset
                    alt="participant item icon"
                    key={`${src}-${index}`}
                    size={size}
                    src={src}
                    tone={tone}
                />
            ))}
            {includeExtraItem ? <SquareAsset alt="extra item icon" size={size} src={extraItemSource} tone={tone} /> : null}
        </div>
    );
}

export function DamageBars({ className = '', compact = false, dealtLabel, dealtPercent, rowClassName = '', takenLabel, takenPercent, tone = 'victory' }) {
    const toneStyles = getToneStyles(tone);
    const rowHeightClass = compact ? 'h-4' : 'h-5';
    const iconClass = compact ? 'h-4 w-4' : 'h-5 w-5';
    const valueClass = compact ? 'text-[10px] font-semibold' : 'text-xs font-bold';
    const trackTextClass = compact ? 'text-zinc-100' : 'text-zinc-100';

    return (
        <div className={`flex flex-col gap-1 ${className}`}>
            <div className={`flex gap-1 ${rowClassName}`}>
                <img
                    alt="damage dealt icon"
                    className={`${iconClass} filter brightness-0 invert`}
                    src={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-sword-icon.svg`}
                />
                <div className={`relative w-full overflow-hidden border ${toneStyles.barTrack}`}>
                    <div
                        className={`${rowHeightClass} ${toneStyles.barFill}`}
                        style={{ width: `${Math.max(0, Math.min(100, dealtPercent || 0))}%` }}
                    />
                    <div className={`absolute inset-y-0 left-0 flex items-center px-1 ${valueClass} ${trackTextClass}`}>{dealtLabel}</div>
                </div>
            </div>
            <div className={`flex gap-1 ${rowClassName}`}>
                <img
                    alt="damage taken icon"
                    className={`${iconClass} filter brightness-0 invert`}
                    src={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-stat-switcher-shield.svg`}
                />
                <div className={`relative w-full overflow-hidden border ${toneStyles.barTrack}`}>
                    <div
                        className={`${rowHeightClass} ${toneStyles.barFill}`}
                        style={{ width: `${Math.max(0, Math.min(100, takenPercent || 0))}%` }}
                    />
                    <div className={`absolute inset-y-0 left-0 flex items-center px-1 ${valueClass} ${trackTextClass}`}>{takenLabel}</div>
                </div>
            </div>
        </div>
    );
}

export function KdaValue({ className = '', iconSize = 14, isPerfectKda = false, value = 0 }) {
    if (isPerfectKda) {
        return (
            <span className={`inline-flex items-center ${className}`}>
                <PiInfinityBold size={iconSize} strokeWidth={iconSize > 14 ? 4 : 2} />
            </span>
        );
    }

    return <span className={`inline-flex items-center ${className}`}>{formatDecimal(value)}</span>;
}

export function HighestStreakBadge({ className = '', highestStreak }) {
    if (!highestStreak) {
        return null;
    }

    const styleMap = {
        penta: 'text-teal-200 bg-teal-900/20 border border-teal-700/40',
        quadra: 'text-emerald-200 bg-emerald-900/20 border border-emerald-700/40',
    };

    return (
        <div className={`inline-flex items-center rounded-md px-1.5 py-[3px] text-center text-[9px] font-semibold uppercase tracking-[0.16em] ${styleMap[highestStreak.style]} ${className}`}>
            {highestStreak.label}
        </div>
    );
}

export function getTierInfo(badge) {
    const normalizedBadge = badge || 'UR';

    if (normalizedBadge.includes('GM')) {
        return {
            colorClass: 'text-red-200',
            iconName: 'Grandmaster',
            shortLabel: 'GM',
        };
    }

    if (normalizedBadge.includes('C')) {
        return {
            colorClass: 'text-yellow-200',
            iconName: 'Challenger',
            shortLabel: 'C',
        };
    }

    if (normalizedBadge.includes('M')) {
        return {
            colorClass: 'text-purple-200',
            iconName: 'Master',
            shortLabel: 'M',
        };
    }

    if (normalizedBadge.includes('D')) {
        return {
            colorClass: 'text-blue-200',
            iconName: 'Diamond',
            shortLabel: 'D',
        };
    }

    if (normalizedBadge.includes('E')) {
        return {
            colorClass: 'text-emerald-200',
            iconName: 'Emerald',
            shortLabel: 'E',
        };
    }

    if (normalizedBadge.includes('P')) {
        return {
            colorClass: 'text-sky-200',
            iconName: 'Platinum',
            shortLabel: 'P',
        };
    }

    if (normalizedBadge.includes('G')) {
        return {
            colorClass: 'text-amber-200',
            iconName: 'Gold',
            shortLabel: 'G',
        };
    }

    if (normalizedBadge.includes('S')) {
        return {
            colorClass: 'text-slate-200',
            iconName: 'Silver',
            shortLabel: 'S',
        };
    }

    if (normalizedBadge.includes('B')) {
        return {
            colorClass: 'text-orange-200',
            iconName: 'Bronze',
            shortLabel: 'B',
        };
    }

    if (normalizedBadge.includes('I')) {
        return {
            colorClass: 'text-zinc-300',
            iconName: 'Iron',
            shortLabel: 'I',
        };
    }

    return {
        colorClass: 'text-white',
        iconName: 'Unranked',
        shortLabel: 'U',
    };
}

const COMPACT_DIVISION_ROMAN_LABELS = {
    1: 'I',
    2: 'II',
    3: 'III',
    4: 'IV',
};

export function formatCompactRankLabel(badge, leaguePoints, showLeaguePoints = true) {
    const normalizedBadge = String(badge || 'UR').toUpperCase();
    const { shortLabel } = getTierInfo(normalizedBadge);

    if (shortLabel === 'U') {
        return 'UR';
    }

    if (showLeaguePoints && ['M', 'GM', 'C'].includes(shortLabel)) {
        return `${shortLabel}-${Number(leaguePoints || 0)}`;
    }

    const divisionNumber = normalizedBadge.match(/(\d+)$/)?.[1];
    return `${shortLabel}${COMPACT_DIVISION_ROMAN_LABELS[Number(divisionNumber)] || ''}`;
}

export function CompactRankLabel({ badge, className = '', leaguePoints, showLeaguePoints = true }) {
    if (!badge) {
        return null;
    }

    const { colorClass } = getTierInfo(badge);

    return (
        <div className={`inline-flex items-center text-[10px] font-semibold uppercase leading-none tracking-[0.08em] ${colorClass} ${className}`}>
            {formatCompactRankLabel(badge, leaguePoints, showLeaguePoints)}
        </div>
    );
}

export function formatAverageRankDisplay(averageElo) {
    if (!averageElo?.badge) {
        return '';
    }

    const { shortLabel } = getTierInfo(averageElo.badge);

    if (averageElo.badge === 'UR') {
        return shortLabel;
    }

    if (averageElo.isApex) {
        return averageElo.shortLabel || `${averageElo.leaguePoints || 0}LP`;
    }

    const divisionLabel = averageElo.shortLabel || averageElo.division || '';
    return `${shortLabel}${divisionLabel}`;
}

export function formatHistoryRankLabel(averageElo) {
    if (!averageElo?.badge) {
        return '';
    }

    const { shortLabel, iconName } = getTierInfo(averageElo.badge);

    if (averageElo.badge === 'UR') {
        return shortLabel;
    }

    if (averageElo.isApex) {
        return `${shortLabel}-${Number(averageElo.leaguePoints || 0)}`;
    }

    const tierLabelMap = {
        Iron: 'IRON',
        Bronze: 'BRON',
        Silver: 'SILV',
        Gold: 'GOLD',
        Platinum: 'PLAT',
        Emerald: 'EMER',
        Diamond: 'DIAM',
    };
    const divisionNumber = String(averageElo.badge || '').match(/(\d+)$/)?.[1];
    const divisionRomanMap = {
        '1': 'I',
        '2': 'II',
        '3': 'III',
        '4': 'IV',
    };
    const divisionLabel = divisionRomanMap[divisionNumber] || averageElo.shortLabel || averageElo.division || '';
    const tierLabel = tierLabelMap[iconName] || shortLabel;

    return `${tierLabel} ${divisionLabel}`.trim();
}

export function renderHistoryEloLabelContent(averageElo) {
    const label = formatHistoryRankLabel(averageElo);

    if (!averageElo?.badge || averageElo.isApex || averageElo.badge === 'UR') {
        return label;
    }

    const [tierLabel, divisionLabel] = label.split(' ');

    if (!tierLabel || !divisionLabel) {
        return label;
    }

    return (
        <>
            <span>{tierLabel}</span>
            <span className="ml-1 font-mono text-[0.92em] font-black tracking-[-0.08em]">
                {divisionLabel}
            </span>
        </>
    );
}

export function renderAverageRankDisplayContent(averageElo) {
    const displayText = formatAverageRankDisplay(averageElo);

    if (!averageElo?.badge || averageElo.isApex || averageElo.badge === 'UR') {
        return displayText;
    }

    const tierLabel = displayText.charAt(0);
    const divisionLabel = displayText.slice(1);

    return (
        <>
            <span className="font-semibold tracking-[0.02em]">{tierLabel}</span>
            <span className="ml-1 font-mono font-black tracking-[-0.08em]">
                {divisionLabel}
            </span>
        </>
    );
}

function formatAbbreviatedEloBadge(badge, shortLabel) {
    const normalizedBadge = badge || 'UR';
    const divisionMatch = normalizedBadge.match(/(\d+)$/);

    if (!divisionMatch || ['M', 'GM', 'C', 'U'].includes(shortLabel)) {
        return shortLabel;
    }

    const romanDivisionMap = {
        1: 'I',
        2: 'II',
        3: 'III',
        4: 'IV',
    };

    const divisionLabel = romanDivisionMap[Number(divisionMatch[1])];

    return divisionLabel ? `${shortLabel}${divisionLabel}` : shortLabel;
}

function renderEloBadgeContent(content, abbreviate) {
    if (typeof content !== 'string') {
        return content;
    }

    if (abbreviate) {
        const compactRomanMatch = content.match(/^([IBSGPED])((?:IV|III|II|I))$/);

        if (compactRomanMatch) {
            const [, tierLabel, divisionLabel] = compactRomanMatch;

            return (
                <>
                    <span className="font-semibold tracking-[0.02em]">{tierLabel}</span>
                    <span className="ml-[1px] font-mono font-black tracking-[-0.08em]">
                        {divisionLabel}
                    </span>
                </>
            );
        }
    }

    const contentParts = content.split(' ');
    const divisionLabel = contentParts.at(-1);
    const tierLabel = contentParts.slice(0, -1).join(' ');

    if (!divisionLabel || !['I', 'II', 'III', 'IV'].includes(divisionLabel) || !tierLabel) {
        return content;
    }

    if (abbreviate) {
        return (
            <>
                <span className="font-semibold tracking-[0.02em]">{tierLabel}</span>
                <span className="ml-1 font-mono font-black tracking-[-0.08em]">
                    {divisionLabel}
                </span>
            </>
        );
    }

    return (
        <>
            <span>{tierLabel}</span>
            <span className="ml-1 font-mono text-[0.92em] font-black tracking-[-0.06em]">
                {divisionLabel}
            </span>
        </>
    );
}

export function EloBadge({ abbreviate = false, badge, className = '', contentClassName = '', iconClassName = '', label, showIcon = false }) {
    const normalizedBadge = badge || 'UR';
    const { colorClass, iconName, shortLabel } = getTierInfo(normalizedBadge);
    const content = label || (abbreviate ? formatAbbreviatedEloBadge(normalizedBadge, shortLabel) : normalizedBadge);
    const textSizeClass = content.length > 12 ? 'text-[8.5px]' : content.length > 9 ? 'text-[9px]' : 'text-[10px]';
    const iconSrc = `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=${iconName}.png`;
    const iconStyle = normalizedBadge === 'UR'
        ? { filter: 'brightness(2.3) contrast(1.25)' }
        : undefined;

    return (
        <div className={`inline-flex items-center text-center font-semibold uppercase tracking-[0.08em] leading-none ${textSizeClass} ${colorClass} ${className}`}>
            {showIcon ? <img alt={`${iconName} tier icon`} className={`mr-1 h-4 w-4 shrink-0 object-contain ${iconClassName}`} src={iconSrc} style={iconStyle} /> : null}
            <span className={contentClassName}>
                {renderEloBadgeContent(content, abbreviate)}
            </span>
        </div>
    );
}

export function ToneBadge({ className = '', compact = false, label, tone = 'victory', withDot = true }) {
    const toneStyles = getToneStyles(tone);
    const sizeClassName = compact ? 'px-2 py-0.5 text-[10px] tracking-[0.14em]' : 'px-2.5 py-1 text-xs tracking-[0.14em]';

    return (
        <div className={`inline-flex items-center gap-2 rounded-md border font-semibold uppercase leading-none ${toneStyles.badgeBackground} ${toneStyles.badgeBorder} ${toneStyles.badgeText} ${sizeClassName} ${className}`}>
            {withDot ? <span className={`h-1.5 w-1.5 rounded-full ${toneStyles.badgeDot}`} /> : null}
            <span>{label}</span>
        </div>
    );
}

function formatOrdinal(value) {
    const normalizedValue = Number(value);
    const lastTwoDigits = normalizedValue % 100;
    const suffix = lastTwoDigits >= 11 && lastTwoDigits <= 13
        ? 'TH'
        : ({ 1: 'ST', 2: 'ND', 3: 'RD' }[normalizedValue % 10] || 'TH');

    return `${normalizedValue}${suffix}`;
}

const PERFORMANCE_RANK_STYLE = {
    first: 'bg-cyan-900/20 border-cyan-700/40 text-cyan-200',
    last: 'bg-rose-900/20 border-rose-700/40 text-rose-200',
    normal: 'bg-zinc-900/65 border-zinc-800 text-zinc-500',
    normalCompact: 'bg-zinc-900/80 border-zinc-700 text-zinc-300',
};

export function PerformanceRankBadge({ className = '', compact = false, rank }) {
    const normalizedRank = Number(rank);

    if (!Number.isInteger(normalizedRank) || normalizedRank < 1 || normalizedRank > 10) {
        return null;
    }

    const rankStyles = normalizedRank === 1
        ? PERFORMANCE_RANK_STYLE.first
        : normalizedRank === 10
            ? PERFORMANCE_RANK_STYLE.last
            : compact
                ? PERFORMANCE_RANK_STYLE.normalCompact
                : PERFORMANCE_RANK_STYLE.normal;

    return (
        <div
            aria-label={`Performance placement: ${formatOrdinal(normalizedRank)} of 10`}
            className={`inline-flex shrink-0 items-center justify-center rounded-md border font-bold leading-none tracking-[0.12em] ${rankStyles} ${compact ? 'h-4 w-[36px] text-[8px]' : 'h-[22px] w-[52px] text-[10px]'} ${className}`}
            title={`Performance placement: ${formatOrdinal(normalizedRank)} of 10`}
        >
            {formatOrdinal(normalizedRank)}
        </div>
    );
}
